import { useQuery } from '@tanstack/react-query';
import { STALE_TIME_DETAIL_MS, STALE_TIME_LIST_MS } from '../../constants/reactQueryStaleTime.ts';
import type { ICareHome, ICareHomeListResponse } from '../../interfaces';
import CareHomeService from '../../services/CareHomeService.ts';
import CaregiverService, { type ICaregiverListResponse } from '../../services/CaregiverService.ts';

export type CaregiversListQueryInput = {
  page: number;
  perPage: number;
  query?: string;
  orderBy?: string;
  sortOrder?: 'asc' | 'desc' | null;
  tags?: string;
  carehomeId?: string;
  isActive: boolean;
};

export const getCaregiversListQueryKey = (input: CaregiversListQueryInput) =>
  [
    'caregivers',
    input.isActive,
    input.carehomeId,
    input.page,
    input.perPage,
    input.query,
    input.orderBy,
    input.sortOrder,
    input.tags,
  ] as const;

export function useCaregiversListQuery(input: CaregiversListQueryInput, enabled: boolean) {
  return useQuery<ICaregiverListResponse>({
    queryKey: getCaregiversListQueryKey(input),
    enabled,
    staleTime: STALE_TIME_LIST_MS,
    queryFn: () =>
      CaregiverService.getCaregivers(
        input.page,
        input.perPage,
        input.query,
        undefined,
        input.orderBy,
        input.sortOrder ?? 'desc',
        input.tags,
        input.carehomeId,
        input.isActive
      ),
  });
}

export const getCareHomesForCaregiversQueryKey = (organisationId?: string) =>
  ['careHomesForCaregiversFilters', organisationId] as const;

export function useCareHomesForCaregiversQuery(organisationId?: string, enabled = true) {
  return useQuery<ICareHomeListResponse>({
    queryKey: getCareHomesForCaregiversQueryKey(organisationId),
    enabled: enabled && !!organisationId,
    staleTime: STALE_TIME_LIST_MS,
    queryFn: () =>
      CareHomeService.getCareHomes({
        organisation_id: organisationId!,
      }),
  });
}

export const getCareHomeForPinLengthQueryKey = (careHomeId?: string) =>
  ['careHomeForCaregiversPinLength', careHomeId] as const;

export function useCareHomeForPinLengthQuery(careHomeId?: string, enabled = true) {
  return useQuery<ICareHome>({
    queryKey: getCareHomeForPinLengthQueryKey(careHomeId),
    enabled: enabled && !!careHomeId,
    staleTime: STALE_TIME_DETAIL_MS,
    queryFn: () => CareHomeService.getCareHome(careHomeId!),
  });
}

